import type {
  GridColDef,
  GridRenderEditCellParams,
} from "@mui/x-data-grid-premium";
import { isUndefined } from "lodash-es";
import {
  DataGridDescriptionEditCell,
  DataGridDescriptionViewCell,
} from "../cells/DataGridDescriptionCell.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { ModifyItemProductFn } from "../useModifyItemProduct.js";
import type { DocDataGridRow, PendingCellEdit } from "../types.js";
import type { ProductColumns__ItemFragment } from "./productColumns.generated.js";

interface Options {
  headerName: string;
  canEdit: boolean;
  modifyItemProduct: ModifyItemProductFn;
}

export function createProductDescriptionColumn<
  T extends DocDataGrid_ItemFragment & ProductColumns__ItemFragment,
>(options: Options): GridColDef<DocDataGridRow<T>> {
  return {
    field: "productDescription",
    headerName: options.headerName,
    width: 160,
    minWidth: 120,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) => {
      const item = row.item;
      if (!item) return "";
      const pending = item.pendingChangeAttributes?.["productDescription"];
      return !isUndefined(pending)
        ? pending
        : (item.product?.description ?? "");
    },
    valueSetter: (value: string, row) => ({
      ...row,
      _pendingEdit: {
        apply: (itemId: string) =>
          options.modifyItemProduct(itemId, { productDescription: value }),
      },
    }),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid" || item.isItemEliminated) return null;
      if (!item.product) return null;
      return (
        <DataGridDescriptionViewCell
          params={params}
          canEdit={options.canEdit}
        />
      );
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid" || item.isItemEliminated) return null;
      return <DataGridDescriptionEditCell {...params} />;
    },
  };
}
